import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { resolvePremiumInviteVipCtaHref } from "@/lib/premiumVipCheckoutDestination";
import { type SupportedLanguage } from "@/config/videoCTAConfig";

/**
 * IMPORTANTE — nomenclatura legacy:
 * - `birthday` (DB / Worker flag `showBirthday`) hoy se REMAPEA semánticamente
 *   a **Mejores Amigos** (IG Close Friends MP checkout). El visitor ve el
 *   mismo botón que cta_type='mejores_amigos'. Las entries DB siguen con
 *   `cta_type='birthday'` para no requerir migración. URL viene de
 *   `mejores_amigos_price_config` (admin elige tier per-geo).
 * - `community` (DB / Worker flag `showCommunity`) hoy se usa como **CTA "Exclusivo"**
 *   apuntando a `/invitacion-premium`. Mismo motivo.
 */
export type WorkerDrivenCtaVariant = "community" | "wishlist" | "checkout" | "mejores_amigos";

export type WorkerDrivenCtaResolution =
  | { mode: "hidden" }
  | { mode: "worker"; variant: WorkerDrivenCtaVariant; url: string }
  | { mode: "default" };

/**
 * Misma prioridad que el CTA del hero en BaseTemplate(Black):
 * hideCta → sin CTA; si no, (showBestFriends || showBirthday) + ctaUrl → Mejores Amigos;
 * showCheckout → MP/Stripe; showCommunity → /invitacion-premium ("Exclusivo");
 * showWishlist → fan/wishlist; si no hay flags, default lo define el caller.
 */
export function resolveWorkerDrivenCta(): WorkerDrivenCtaResolution {
  const cfg = getServerTemplateConfig();
  if (!cfg) return { mode: "default" };
  if (cfg.hideCta) return { mode: "hidden" };

  const isExterior = cfg.country && cfg.country !== "AR" && cfg.country !== "UNKNOWN";

  // Defensa AR: aunque showDual esté presente (por desync o cta_type=dual en DB),
  // AR debe usar los CTAs de hero provincia (community/exclusivo etc). Nunca "default"
  // que podría saltar a video dual.
  if (cfg.country === "AR" && cfg.showDual) {
    // Tratar dual como community para AR (botón Exclusivo /invitacion-premium).
    return { mode: "worker", variant: "community", url: "/invitacion-premium" };
  }

  // Política exterior: para visitantes !AR, ignoramos los flags legacy de wishlist/birthday/community
  // (que históricamente llevaban a fan.*). El flujo debe ser SIEMPRE video Mux (DualCTAVideoLanding
  // o MuxVideoPlayerWithCTA) → conversión vía Stripe embebido. Forzamos "default" para que el
  // caller (MuxVideoPlayerWithCTA etc.) use su lógica de Stripe/checkout por país.
  if (isExterior) {
    // Aún así respetamos hideCta explícito (ya retornado arriba). Si showDual u otros,
    // el DualCTAVideoLanding es self-contained y no usa este resolve para navegación.
    // Cualquier showWishlist/etc para exterior se trata como default (sin CTA worker-driven a fan).
    return { mode: "default" };
  }

  // "Mejores Amigos" (cta_type=mejores_amigos OR cta_type=birthday legacy).
  // Admin elige tier (Precio Medio/Alto) per-geo en /admin/cta-por-region;
  // el url MP viaja en `ctaUrl`. Sin url no se rendea (mismo guard que hero).
  // showBirthday también triggea este branch — remap semántico 2026-06-20:
  // las entries legacy con cta_type='birthday' rinden Mejores Amigos.
  if ((cfg.showBestFriends || cfg.showBirthday) && cfg.ctaUrl) {
    return {
      mode: "worker",
      variant: "mejores_amigos",
      url: cfg.ctaUrl,
    };
  }

  // "Ser Exclusivo" (cta_type=checkout) — mismo destino de pago que DualCTA/Reels:
  // MP preapproval (AR) o /checkout-channel-invite (intl); sin /membresia-vip.
  if (cfg.showCheckout) {
    const country = cfg.country || "US";
    return {
      mode: "worker",
      variant: "checkout",
      url: resolvePremiumInviteVipCtaHref(country),
    };
  }
  if (cfg.showCommunity) {
    return {
      mode: "worker",
      variant: "community",
      url: "/invitacion-premium",
    };
  }
  if (cfg.showWishlist) {
    return {
      mode: "worker",
      variant: "wishlist",
      url: "",
    };
  }
  return { mode: "default" };
}

const WORKER_CTA_LABELS: Record<
  WorkerDrivenCtaVariant,
  Record<SupportedLanguage, string>
> = {
  // `community` slot → ahora etiquetado como "Exclusivo" (apunta a /invitacion-premium).
  community: {
    es: "Exclusivo",
    en: "Exclusive",
    pt: "Exclusivo",
    fr: "Exclusif",
    de: "Exklusiv",
    it: "Esclusivo",
    nl: "Exclusief",
    tr: "Özel",
    ar: "حصري",
    pl: "Ekskluzywny",
    ja: "エクスクルーシブ",
    ko: "익스클루시브",
    id: "Eksklusif",
  },
  wishlist: {
    es: "Ingresa a mi wishlist de cumpleaños",
    en: "Open my birthday wishlist",
    pt: "Ver minha lista de presentes",
    fr: "Voir ma wishlist d'anniversaire",
    de: "Zur Geburtstags-Wishlist",
    it: "Apri la mia wishlist di compleanno",
    nl: "Bekijk mijn verlanglijst",
    tr: "Doğum günü listemi aç",
    ar: "افتح قائمة أمنياتي",
    pl: "Otwórz moją listę życzeń",
    ja: "誕生日リストを開く",
    ko: "생일 위시리스트 열기",
    id: "Buka wishlist ulang tahun",
  },
  // `checkout` slot → "Ser Exclusivo" — botón directo a checkout payment.
  checkout: {
    es: "Ser Exclusivo",
    en: "Become Exclusive",
    pt: "Ser Exclusivo",
    fr: "Devenir Exclusif",
    de: "Exklusiv werden",
    it: "Diventa Esclusivo",
    nl: "Word Exclusief",
    tr: "Özel Ol",
    ar: "كن حصرياً",
    pl: "Zostań Ekskluzywny",
    ja: "エクスクルーシブになる",
    ko: "익스클루시브 되기",
    id: "Jadilah Eksklusif",
  },
  // `mejores_amigos` → IG Close Friends paid list (MP checkout en AR).
  // Espeja la i18n key `baseTemplate.heroCtaLabelBestFriends` en las 5 locales
  // del sitio; el resto traducidos para que video/bio overlays no caigan al fallback en.
  mejores_amigos: {
    es: "Mejores Amigos",
    en: "Best Friends",
    pt: "Melhores Amigos",
    fr: "Amis Proches",
    de: "Beste Freunde",
    it: "Migliori Amici",
    nl: "Beste Vrienden",
    tr: "En Yakın Arkadaşlar",
    ar: "أعز الأصدقاء",
    pl: "Najlepsi Przyjaciele",
    ja: "親友",
    ko: "친한 친구",
    id: "Sahabat Dekat",
  },
};

export function getWorkerCtaDisplayLabel(
  language: string | undefined,
  variant: WorkerDrivenCtaVariant,
): string {
  const key = (language?.split("-")[0]?.toLowerCase() as SupportedLanguage) || "en";
  const row = WORKER_CTA_LABELS[variant];
  return row[key] ?? row.en;
}
